import React from "react";
import { useVersion, useBuildInfo } from "@/hooks/useVersionInfo";
import { resolveDeploymentEnvironment } from "@/lib/environment";

interface VersionProps {
  /**
   * Custom CSS class name for styling
   */
  className?: string;

  /**
   * Whether to show the 'v' prefix before version number
   * @default true
   */
  showPrefix?: boolean;

  /**
   * Whether to show build information on hover
   * @default true
   */
  showBuildInfo?: boolean;

  /**
   * Additional build information to display
   */
  buildInfo?: {
    buildNumber?: string;
    buildDate?: string;
    commit?: string;
  };
}

/**
 * Version Display Component
 *
 * Shows the current application version from environment variables.
 * Automatically reads VITE_APP_VERSION and displays it with optional build info.
 *
 * @example
 * ```tsx
 * // Basic usage
 * <Version />
 *
 * // Custom styling
 * <Version className="text-sm text-muted-foreground" />
 *
 * // Without prefix
 * <Version showPrefix={false} />
 *
 * // With build info
 * <Version buildInfo={{ buildNumber: "123", commit: "abc123" }} />
 * ```
 */
const Version: React.FC<VersionProps> = ({
  className = "text-xs text-muted-foreground",
  showPrefix = true,
  showBuildInfo = true,
  buildInfo,
}) => {
  // Get version from environment variable with fallback
  const version = import.meta.env.VITE_APP_VERSION || "1.0.0";

  // Build display text
  const displayVersion = showPrefix ? `v${version}` : version;

  // Build hover title with additional info
  const buildTitle = React.useMemo(() => {
    if (!showBuildInfo) {
      return displayVersion;
    }

    const parts = [`Version: ${version}`];

    // Add build info if available
    if (buildInfo?.buildNumber) {
      parts.push(`Build: #${buildInfo.buildNumber}`);
    }

    if (buildInfo?.buildDate) {
      parts.push(`Built: ${buildInfo.buildDate}`);
    }

    if (buildInfo?.commit) {
      parts.push(`Commit: ${buildInfo.commit}`);
    }

    // Add environment info — the deployment environment, not the build mode,
    // which reads "production" on every deployed site including dev (#767).
    const environment = resolveDeploymentEnvironment();
    if (environment) {
      parts.push(`Environment: ${environment}`);
    }

    return parts.join("\n");
  }, [version, buildInfo, showBuildInfo, displayVersion]);

  // Handle click to copy version info
  const handleClick = React.useCallback(() => {
    const versionInfo = {
      version,
      environment: resolveDeploymentEnvironment(),
      mode: import.meta.env.MODE,
      buildInfo,
      timestamp: new Date().toISOString(),
    };

    // Try to copy to clipboard
    if (navigator.clipboard) {
      navigator.clipboard
        .writeText(JSON.stringify(versionInfo, null, 2))
        .then(() => {
          // Could show a toast notification here
          console.warn("Version info copied to clipboard");
        })
        .catch(err => {
          console.warn("Failed to copy version info:", err);
        });
    } else {
      // Fallback for older browsers
      console.warn("Version info:", versionInfo);
    }
  }, [version, buildInfo]);

  return (
    <button
      type="button"
      className={`${className} cursor-pointer select-none transition-opacity hover:opacity-80 appearance-none bg-transparent border-0 p-0 font-inherit`}
      title={buildTitle}
      onClick={handleClick}
      aria-label={`Application version ${version}. Click to copy version info.`}
    >
      {displayVersion}
    </button>
  );
};

export default Version;

/**
 * Component for displaying detailed version info in development
 */
export const VersionDebug: React.FC = () => {
  const { version, mode, semanticVersion } = useVersion();
  const buildInfo = useBuildInfo();

  // Only show in development
  if (mode === "production") {
    return null;
  }

  return (
    <div className="fixed bottom-2 right-2 bg-black/80 text-white text-xs p-2 rounded font-mono z-50">
      <div>Version: {version}</div>
      <div>Mode: {mode}</div>
      <div>
        Year/Month: {semanticVersion.year}.{semanticVersion.month}
      </div>
      <div>Patch: {semanticVersion.patch}</div>
      {buildInfo.hasInfo && (
        <>
          {buildInfo.buildNumber && <div>Build: #{buildInfo.buildNumber}</div>}
          {buildInfo.buildDate && <div>Built: {buildInfo.buildDate}</div>}
          {buildInfo.commit && <div>Commit: {buildInfo.commit.substring(0, 8)}</div>}
        </>
      )}
    </div>
  );
};
